import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

const OAUTH_CLIENTS_QUERY_KEY = 'oauthClients';

/**
 * A registered third-party OAuth app. Descriptive fields (name, description, redirect URIs, scopes)
 * are sourced from WorkOS; identity/ownership/timestamps come from the local pointer. PKCE public
 * clients have no secret, so none is returned.
 */
export interface IOAuthClient extends Api.IBaseResourceModel {
	clientId: string;
	name: string;
	description?: string;
	redirectUris: string[];
	scopes: string[];
	owners: string[];
}

export function useGetOAuthClients({
	enabled,
	refetchOnWindowFocus = false,
}: {
	enabled: boolean;
	refetchOnWindowFocus?: boolean;
}) {
	const userSession = useUserSession();
	return useQuery({
		enabled,
		refetchOnWindowFocus,
		queryFn: () =>
			userSession.webServiceHelper.callAsync<IOAuthClient[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: 'oauthclients',
				}),
				'GET'
			),
		queryKey: [OAUTH_CLIENTS_QUERY_KEY],
		meta: {
			toastErrorMessage: 'Failed to load OAuth clients',
		},
	});
}

export const invalidateGetOAuthClients = () =>
	reactQueryClient.invalidateQueries({ queryKey: [OAUTH_CLIENTS_QUERY_KEY] });
